/**
 * useDetail hook.
 */
import ChannelUtils from '@shared/utils/channels';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import Rail from '@shared/classes/rail';
import Routes from '@shared/types/enums/routes';
import detailRequest from '@shared/api/detail/detailRequest';
import useComplete from '@shared/components/utils/hooks/useComplete';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import useMetadata from '@shared/components/utils/hooks/useMetadata';
import { Coordinates } from '@shared/utils/floatingMenu';
import { DetailContextProps } from '@shared/components/utils/context/detailContext';
import { openFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { selectCurrentParams } from '@shared/store/slices/router.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Custom hook for the Detail component. Returns the properties required for the template.
 */
export default function useDetail(): DetailContextProps {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const currentParams = useAppSelector(selectCurrentParams);
  const { isAnonymous } = useAppSelector(userSelector);

  // State
  const [asset, setAsset] = useState<Event | null>(
    currentParams?.asset as Event
  );
  const [complete, setComplete] = useComplete();
  const [related, setRelated] = useState<Rail | null>(null);

  // Detail data
  const { upgradablePlan } = useMemo(() => {
    const upgradablePlan = ChannelUtils.getUpgradablePlan(
      asset?.channel,
      false,
      asset?.type
    );

    return {
      upgradablePlan,
    };
  }, [asset, isAnonymous]);

  // Refs
  const assetRef = useRef(asset);
  const focusableRef = useRef<{
    buttons: boolean;
    synopsis: boolean;
    related: boolean;
  }>({
    buttons: false,
    synopsis: false,
    related: false,
  });
  const focusedItemRef = useRef<number | number[] | null>(null);
  assetRef.current = asset;

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Fetches the detail data.
   */
  useEffect(() => {
    detailRequest(
      (currentParams?.asset as Event)?.id,
      (currentParams?.asset as Event)?.eventId
    ).then((item) => {
      if (item?.asset) setAsset(item?.asset);
      if (item?.rail?.data?.length) setRelated(item?.rail);
      setComplete(true);
    });
    return () => {
      setComplete(false);
      setAsset(null);
      setRelated(null);
      focusableRef.current.buttons = false;
      focusableRef.current.synopsis = false;
      focusableRef.current.related = false;
    };
  }, [currentParams]);

  /**
   * Handles focus on buttons or synopsis upon completion.
   */
  useEffect(() => {
    if (!complete) return;
    if (focusableRef.current.buttons) {
      dispatch(setFocus(Focus.DETAIL_BUTTONS));
      focusedItemRef.current = Focus.DETAIL_BUTTONS;
      return;
    }
    if (focusableRef.current.synopsis) {
      dispatch(setFocus(Focus.DETAIL_SYNOPSIS));
      focusedItemRef.current = Focus.DETAIL_SYNOPSIS;
      return;
    }
    dispatch(setFocus(Focus.DETAIL_BUTTONS));
    focusedItemRef.current = Focus.DETAIL_BUTTONS;
  }, [complete]);

  // ------------------------------------------------------------
  // Keyboard handlers
  // ------------------------------------------------------------

  /**
   * Handles key down events for detail controls and navigation.
   */
  const keyDownHandler = (key: string) => {
    const { focus: focusSlice } = store.getState();
    // Store focus state
    const areButtonsFocused = focusSlice?.focus === Focus.DETAIL_BUTTONS;
    const areRelatedFocused = focusSlice?.focus === Focus.RAILS_LIST;
    const isMenuFocused = focusSlice?.focus === Focus.MENU;
    const isSynopsisFocused = focusSlice?.focus === Focus.DETAIL_SYNOPSIS;
    // Store focusable state
    const hasButtons = focusableRef.current.buttons;
    const hasRelated = focusableRef.current.related;
    const hasSynopsis = focusableRef.current.synopsis;

    // Determine new focus
    let newFocus = null;
    switch (key) {
      case 'BACK':
        // If buttons, synopsis, or related are focused, pop route
        if (areButtonsFocused || isSynopsisFocused || areRelatedFocused)
          dispatch(popRoute());
        break;
      case 'LEFT':
        // Left key is handled by the components themselves
        break;
      case 'RIGHT':
        // If menu is focused, focus the previous focus
        if (isMenuFocused) {
          newFocus = focusedItemRef.current || Focus.DETAIL_BUTTONS;
          dispatch(setFocus(newFocus));
          focusedItemRef.current = newFocus;
        }
        break;
      case 'DOWN':
        // Buttons are focused -> Focus synopsis
        if (areButtonsFocused && hasSynopsis) newFocus = Focus.DETAIL_SYNOPSIS;
        // Buttons or synopsis are focused -> Focus related
        else if ((areButtonsFocused || isSynopsisFocused) && hasRelated)
          newFocus = Focus.RAILS_LIST;
        break;
      case 'UP':
        // Synopsis is focused
        if (isSynopsisFocused) {
          // Has buttons -> Focus buttons
          if (hasButtons) newFocus = Focus.DETAIL_BUTTONS;
        }
        // Related are focused
        else if (areRelatedFocused) {
          // Has synopsis -> Focus synopsis
          if (hasSynopsis) newFocus = Focus.DETAIL_SYNOPSIS;
          // No synopsis -> Focus buttons
          else newFocus = Focus.DETAIL_BUTTONS;
        }
        break;
    }

    // Set new focus and focused item reference
    if (newFocus) {
      dispatch(setFocus(newFocus));
      focusedItemRef.current = newFocus;
    }
  };

  /**
   * Handles key press events for detail controls and navigation.
   */
  const keyPressHandler = () => {
    const { floatingMenu } = store.getState();
    if (!floatingMenu.open) {
      dispatch(
        openFloatingMenu({
          route: Routes.DETAIL,
          asset: assetRef.current,
          position: Coordinates.DETAIL,
        })
      );
    }
  };

  // ------------------------------------------------------------
  // Custom hooks
  // ------------------------------------------------------------

  /**
   * Asset info hook.
   */
  const metadata = useMetadata({ asset, extended: true });

  /**
   * Keyboard hook. Handles key down, press, and up events for detail controls and navigation.
   */
  useKeyboard({
    canBeFocused: false,
    keyDownHandler,
    keyPressHandler,
  });

  // ------------------------------------------------------------
  // Return
  // ------------------------------------------------------------

  return {
    asset,
    complete,
    focusable: focusableRef,
    metadata: asset ? metadata : null,
    related,
    upgradablePlan,
  };
}
